<template>
    <v-col class="stats" cols="12">
        <p class="mb-0" v-if="normalizedOptions.availability.shown">
            <strong>{{ normalizedOptions.availability.label }}</strong>

            <span>
                {{ availability }}
            </span>
        </p>
        <p class="mb-0" v-if="normalizedOptions.bedrooms.shown">
            <strong>{{ normalizedOptions.bedrooms.label }}</strong>

            <span>
                {{ bedrooms }}
            </span>
        </p>

        <template
            v-if="
                has(listing, 'widgetSiteSettings.customPrices') &&
                listing.widgetSiteSettings.customPrices.length > 0
            "
        >
            <p
                class="mb-0"
                v-for="customPrice in listing.widgetSiteSettings.customPrices"
                :key="customPrice.id"
            >
                <strong>{{ customPrice.label }}</strong>
                <span>
                    {{ currencyFormatter.format(customPrice.value) }}
                </span>
            </p>
        </template>

        <template v-else>
            <p
                class="mb-0 minshare"
                v-if="
                    !isPrivateSale &&
                    minSharePercentage &&
                    normalizedOptions.sharePercentage.shown

                "
        ><strong class="minShareLabel">{{
                    normalizedOptions.sharePercentage.label
                }}</strong>
                <span>
                    {{ minSharePercentage }}
                </span>
            </p>
            <p
                class="mb-0"
                v-if="monthlyRent && normalizedOptions.monthlyRent.shown"
            >
                <strong>{{ normalizedOptions.monthlyRent.label }}</strong>
                <span>
                    {{ monthlyRent }}
                </span>
            </p>
            <p
                class="mb-0"
                v-if="fullMarketValue && normalizedOptions.price.shown"
            >
                <strong>{{
                    !isPrivateSale
                        ? normalizedOptions.price.label
                        : normalizedOptions.price.labelAlt
                }}</strong>
                <span>
                    {{ fullMarketValue }}
                </span>
            </p>
        </template>

        <hr class="vd-md-none my-4" />
    </v-col>
</template>

<script>
import { defineComponent, computed, ref } from "@vue/composition-api";
import { has, merge } from "lodash";

export default defineComponent({
    props: {
        listing: {
            type: Object,
        },
        options: {
            type: Object,
        },
    },
    setup(props) {
        const defaultOptions = ref({
            availability: {
                label: "Availability",
                shown: true,
            },
            bedrooms: {
                label: "Bedrooms",
                shown: true,
            },
            bathrooms: {
                shown: false,
            },
            deposit: {
                shown: false,
            },
            sharePercentage: {
                label: "Min share",
                shown: true,
            },
            share: {
                shown: false,
            },
            price: {
                shown: true,
                label: "Full market value",
                labelAlt: "Prices from",
                field: "fullMarketPrice",
            },
            monthlyRent: {
                shown: true,
                label: "Monthly rent from",
            },
        });

        const currencyFormatter = new Intl.NumberFormat("gb-GB", {
            style: "currency",
            currency: "GBP",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });

        const normalizedOptions = computed(() => {
            return merge(defaultOptions.value, props.options);
        });

        const availability = computed(() => {
            if (has(props, "listing.availabilityStatus.name")) {
                return props.listing.availabilityStatus.name;
            }
            return "TBC";
        });

        const bedrooms = computed(() => {
            if (has(props, "listing.bedroomsFormatted")) {
                return props.listing.bedroomsFormatted.replace("0", "Studio");
            }
            return "TBC";
        });

        const isPrivateSale = computed(() => {
            if (has(props, "listing.schemeGroup")) {
                const slug = props.listing.schemeGroup.slug;
                return slug == "private-sale" || slug == "open-market-sale";
            }

            return false;
        });

        const minSharePercentage = computed(() => {
            if (!props.listing.forSale) {
                return null;
            }

            let returnString = "TBC";
            if (
                has(props, "listing.availabilityStatus.code") &&
                has(props, "listing.availableMinSharePercentage")
            ) {
                if (
                    props.listing.availableMinSharePercentage &&
                    props.listing.availabilityStatus.code != "coming-soon"
                ) {
                    returnString =
                        props.listing.availableMinSharePercentage + "%";
                }
            }

            return returnString;
        });

        const monthlyRent = computed(() => {
            if (props.listing.forSale) {
                return null;
            }

            if (
                has(props, "listing.availabilityStatus.code") &&
                has(props, "listing.monthlyRent")
            ) {
                if (
                    props.listing.availabilityStatus.code == "coming-soon" ||
                    !props.listing.monthlyRent
                ) {
                    return "TBC";
                } else {
                    return currencyFormatter.format(props.listing.monthlyRent);
                }
            } else {
                return "TBC";
            }
        });

        const fullMarketValue = computed(() => {
            if (!props.listing.forSale) {
                return null;
            }

            if (
                has(props, "listing.availabilityStatus.code") &&
                has(props, "listing.fullMarketPrice")
            ) {
                if (props.listing.availabilityStatus.code == "coming-soon") {
                    return "TBC";
                } else if (has(normalizedOptions.value, "price.field")) {
                    const fieldToShow = normalizedOptions.value.price.field;
                    if (has(props, "listing." + fieldToShow)) {
                        return props.listing[fieldToShow]
                            ? currencyFormatter.format(
                                  props.listing[fieldToShow]
                              )
                            : "TBC";
                    } else {
                        return props.listing.fullMarketPrice
                            ? currencyFormatter.format(
                                  props.listing.fullMarketPrice
                              )
                            : "TBC";
                    }
                } else {
                    return currencyFormatter.format(
                        props.listing.fullMarketPrice
                    );
                }
            } else {
                return "TBC";
            }
        });

        return {
            isPrivateSale,
            bedrooms,
            minSharePercentage,
            monthlyRent,
            fullMarketValue,
            normalizedOptions,
            availability,
            has,
            currencyFormatter,
        };
    },
});
</script>

<style lang="scss">
@import "~vuetify/src/styles/styles.sass";

.stats {
    .v-icon {
        vertical-align: sub;
    }

    strong {
        font-weight: 500;
    }

    span {
        float: right;
        width: 40%;
        text-align: left;

        @media #{map-get($display-breakpoints, 'xs-only')} {
            text-align: right;
        }
    }
}
</style>
